import { graphHooks } from '@tunasong/graph-lib/react'
import { Button, fillCopy, requestAccessCopy as copy, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { AccessRequestRow } from '../share/access-request-row.js'
import { useAccessRequestActions } from '../share/use-access-request-actions.js'
import { useRefreshOnAccessRequestNotification } from '../share/use-refresh-on-access-request-notification.js'
import { useRowSettleFocus } from '../share/use-row-settle-focus.js'
import styles from './waiting-on-you-section.module.css'

/** Rows shown before "Show all", as Unfiled (§8.5) */
const waitingCap = 3

/** The Continue section's heading: where focus goes when this section empties (§8.9 #6) */
const continueHeadingId = 'home-continue'

/**
 * Home, between the header and Continue: the only thing someone else is blocked on (request-access spec §8.5).
 * Renders nothing when empty. It reads the same table as the Share dialog, so acting here clears it there.
 */
export const WaitingOnYouSection: FC = () => {
  useRefreshOnAccessRequestNotification()
  const { data: requests } = graphHooks.useOwnerAccessRequests()
  const actions = useAccessRequestActions()
  const [expanded, setExpanded] = useState(false)
  const [refocusToggle, setRefocusToggle] = useState(false)
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const count = requests?.length ?? 0
  const { listRef, settle } = useRowSettleFocus({ count, fallbackIds: [continueHeadingId] })

  /** "Show fewer" collapses in place and hands focus back to "Show all {n}" (§8.9 #2) */
  useEffect(() => {
    if (refocusToggle) {
      toggleRef.current?.focus()
      setRefocusToggle(false)
    }
  }, [refocusToggle])

  const handleToggle = () => {
    if (expanded) {
      setRefocusToggle(true)
    }
    setExpanded(!expanded)
  }

  if (!requests || count === 0) {
    return <>{actions.toast}</>
  }
  const visible = expanded ? requests : requests.slice(0, waitingCap)

  return (
    <section className={styles.section} aria-labelledby="home-waiting-on-you">
      <Typography id="home-waiting-on-you" variant="h2" className={styles.heading}>
        {copy.homeHeading}
      </Typography>
      <ul id="home-waiting-on-you-list" ref={listRef} className={styles.list}>
        {visible.map((view, index) => (
          <AccessRequestRow
            key={`${view.entity.id}-${view.requester.userId}`}
            view={view}
            layout="home"
            actions={actions}
            onSettled={() => settle(index)}
          />
        ))}
      </ul>
      {count > waitingCap ? (
        <Button
          ref={toggleRef}
          className={styles.toggle}
          aria-expanded={expanded}
          aria-controls="home-waiting-on-you-list"
          onClick={handleToggle}
        >
          {expanded ? copy.showFewer : fillCopy(copy.showAll, { n: count })}
        </Button>
      ) : null}
      {actions.toast}
    </section>
  )
}

export default WaitingOnYouSection
